import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { FAILURE, DEFAULT } from '../../../common/ui/constants';
import type {
	CreateOrEditPriorityScheme,
	Priority,
	PrioritySchemeFormState,
} from '../../../common/ui/types';
import messages from './messages';
import { NameAndDescriptionView } from './name-and-description';
import Priorities from './priorities';

export type PrioritySchemeDetailsProps = {
	prioritySchemeFormState: PrioritySchemeFormState;
	/**
	 * State which will be used to call APIs
	 */
	priorityScheme: CreateOrEditPriorityScheme;
	setPriorityScheme: (priorityScheme: CreateOrEditPriorityScheme) => void;
	nameError: string;
	descriptionError: string;
	prioritiesError: string;
	/**
	 * Given a priority Id, remove all mappings that are mapped to that priority
	 * @param removedPriorityId the priority that was removed from mapping list
	 */
	onRemovePriorityMapping?: (removedPriorityId: string) => void;
};

export const PrioritySchemeDetails = ({
	prioritySchemeFormState,
	priorityScheme,
	setPriorityScheme,
	nameError,
	descriptionError,
	prioritiesError,
	onRemovePriorityMapping,
}: PrioritySchemeDetailsProps) => {
	const { formatMessage } = useIntl();

	if (prioritySchemeFormState === FAILURE) {
		return <></>;
	}

	const onPrioritiesChange = (priorities: Priority[]) => {
		setPriorityScheme({
			...priorityScheme,
			priorities,
		});
	};

	return (
		<Container>
			<TabPanelDescription>
				{formatMessage(messages.prioritySchemeDetailsTabPanelDescription, {
					// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
					b: (...chunks: ReactNode[]) => <strong>{chunks}</strong>,
				})}
			</TabPanelDescription>
			<NameAndDescriptionView
				priorityScheme={priorityScheme}
				nameError={nameError}
				descriptionError={descriptionError}
				setPriorityScheme={setPriorityScheme}
				isDisabled={prioritySchemeFormState !== DEFAULT}
			/>
			<Priorities
				error={prioritiesError}
				onChange={onPrioritiesChange}
				priorities={priorityScheme.priorities}
				onRemovePriorityMapping={onRemovePriorityMapping}
			/>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabPanelDescription = styled.p({
	paddingTop: token('space.100', '8px'),
});
